import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { API_ERROR_CODES, isApiErrorResponse, type ApiErrorResponse } from '@hrms/shared';
import { env } from './env';
import { clearAccessToken, getAccessToken, setAccessToken } from './access-token';

/** Auth routes must never trigger the refresh-and-retry cycle. */
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password'];

interface RetryableConfig extends InternalAxiosRequestConfig {
  /** Set once a request has already been replayed after a refresh. */
  _retried?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  // Required for the httpOnly refresh cookie to travel with /auth/* requests.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// --- Silent refresh ---------------------------------------------------------
//
// A page typically fires several requests at once. When the access token has
// expired they all 401 together, and a naive implementation would fire one
// refresh per failure — burning the single-use refresh token N times over. The
// second of those looks exactly like a replay to the API, which revokes the
// whole token family and logs the user out.
//
// So: the first 401 starts a refresh, everyone else awaits the same promise.

let refreshPromise: Promise<string> | null = null;
let sessionExpiredHandler: (() => void) | null = null;

/** Registered by `AuthProvider` to route the user back to sign-in. */
export function onSessionExpired(handler: () => void): void {
  sessionExpiredHandler = handler;
}

async function refreshAccessToken(): Promise<string> {
  // A bare axios call, not `api` — going through the instance would re-enter
  // the response interceptor and recurse if the refresh itself 401s.
  const response = await axios.post<{ accessToken: string }>(
    `${env.VITE_API_BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true },
  );

  const token = response.data.accessToken;
  setAccessToken(token);
  return token;
}

function beginRefresh(): Promise<string> {
  refreshPromise ??= refreshAccessToken().finally(() => {
    // Cleared regardless of outcome so a later request can try again rather
    // than forever awaiting a settled, rejected promise.
    refreshPromise = null;
  });

  return refreshPromise;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    const isAuthPath = AUTH_PATHS.some((path) => config?.url?.includes(path));
    const shouldAttemptRefresh = status === 401 && config && !config._retried && !isAuthPath;

    if (!shouldAttemptRefresh) {
      return Promise.reject(toApiError(error));
    }

    config._retried = true;

    try {
      const token = await beginRefresh();
      config.headers.Authorization = `Bearer ${token}`;
      return await api.request(config);
    } catch {
      // The refresh cookie is gone, expired, or was revoked as a replay.
      clearAccessToken();
      sessionExpiredHandler?.();
      return Promise.reject(toApiError(error));
    }
  },
);

/**
 * Normalises anything axios can throw into the API's own error shape.
 *
 * Callers then have one type to handle instead of branching on network error
 * versus HTTP error versus a response body that is not JSON at all (an nginx
 * 502 page, say).
 */
export function toApiError(error: unknown): ApiErrorResponse {
  if (isApiErrorResponse(error)) return error;

  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (isApiErrorResponse(data)) return data;

    if (error.code === 'ECONNABORTED') {
      return synthetic(408, API_ERROR_CODES.INTERNAL_ERROR, 'The request timed out.');
    }

    if (!error.response) {
      return synthetic(
        0,
        API_ERROR_CODES.INTERNAL_ERROR,
        'Cannot reach the server. Check your connection.',
      );
    }

    return synthetic(
      error.response.status,
      API_ERROR_CODES.INTERNAL_ERROR,
      error.response.status >= 500
        ? 'Something went wrong on our end. Please try again.'
        : error.message,
    );
  }

  return synthetic(
    0,
    API_ERROR_CODES.INTERNAL_ERROR,
    error instanceof Error ? error.message : 'An unexpected error occurred.',
  );
}

/** `lastOdometer` → `Last odometer`; `lines.0.amount` → `Lines 1 · amount`. */
function humaniseField(path: string): string {
  return path
    .split('.')
    // An array index is 0-based in the path and 1-based to a person counting
    // rows on their screen.
    .map((part) => (/^\d+$/.test(part) ? `#${Number(part) + 1}` : part))
    .map((part) => part.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase())
    .join(' ')
    .replace(/^./, (character) => character.toUpperCase());
}

/**
 * The message to actually show a person.
 *
 * A failed validation comes back as `message: "Validation failed"` with the
 * useful part in `errors`, keyed by field. Toasting the message alone — which
 * every form here did, at sixty-nine call sites — tells somebody their input
 * was refused and nothing whatsoever about which field or why. A vehicle
 * rejected for an odometer of `28667.7` said only "Validation failed".
 *
 * The field detail replaces the generic sentence rather than appending to it:
 * "Validation failed — Odometer: expected integer" says the same thing twice.
 */
export function toApiErrorMessage(error: unknown): string {
  // Through `fieldErrors` rather than reading `errors` directly, so there is one
  // definition of "this was a validation failure" and a toast cannot disagree
  // with the form fields about it.
  const detail = Object.entries(fieldErrors(error))
    .flatMap(([field, messages]) => messages.map((message) => `${humaniseField(field)}: ${message}`))
    .join(' · ');

  return detail || toApiError(error).message;
}

function synthetic(
  statusCode: number,
  code: ApiErrorResponse['code'],
  message: string,
): ApiErrorResponse {
  return { statusCode, code, message, timestamp: new Date().toISOString() };
}

/** Field-level messages for a form, or `{}` when the failure was not validation. */
export function fieldErrors(error: unknown): Record<string, string[]> {
  const apiError = toApiError(error);
  return apiError.code === API_ERROR_CODES.VALIDATION_FAILED ? (apiError.errors ?? {}) : {};
}

// --- Thin typed helpers -----------------------------------------------------

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return (await api.get<T>(url, config)).data;
}

export async function post<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (await api.post<T>(url, body, config)).data;
}

/**
 * Multipart POST, for the few endpoints that carry a file.
 *
 * `Content-Type` is deleted rather than set: the browser has to write it
 * itself so it can append the multipart boundary. Setting it by hand — or
 * leaving the client's `application/json` default in place — produces a body
 * the server cannot parse, and the failure looks like a validation error rather
 * than a header problem.
 */
export async function postForm<T>(
  url: string,
  form: FormData,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (
    await api.post<T>(url, form, {
      ...config,
      headers: { ...config?.headers, 'Content-Type': undefined },
    })
  ).data;
}

export async function patch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  return (await api.patch<T>(url, body, config)).data;
}

export async function put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return (await api.put<T>(url, body, config)).data;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return (await api.delete<T>(url, config)).data;
}
